import { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { PasswordMeterComponent } from "../../assets/ts/components";
import InputTextField from "../../components/forms/InputTextField";
import FormButton from "../../components/forms/FormButton";
import { getCountries, signup } from "services/authRequests";
import { useTranslation } from "react-i18next";
import { useAuthValidationRules } from "helpers/validation-schema/AuthValidationRules";
import { ERROR_STATUS_KEY, SUCCESS_CODE } from "constant/errorCode";
import { SelectionOption } from "helpers/models/auth_models";
import SearchableDropdown from "components/forms/SearchableDropdown";
import { setRegion } from "helpers/utils";
import { updateHttpClientBaseUrl } from "services/apiInterceptor";

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  companyName: "",
  countryGuid: "",
};

export function Registration() {
  const { t } = useTranslation();
  let navigate = useNavigate();
  const validationRules = useAuthValidationRules();
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState<SelectionOption[]>([]);
  const [countryWithRegion, setCountryWithRegion] = useState<any[]>([]);
  const registrationSchema = Yup.object().shape({
    firstName: validationRules.firstNameValidationSchema,
    lastName: validationRules.lastNameValidationSchema,
    companyName: validationRules.companyValidationSchema,
    email: validationRules.emailValidationSchema,
    password: validationRules.passwordValidationSchema,
  });

  const fetchCountries = async () => {
    try {
      await getCountries()
        .then((res: any) => {
          setCountry(
            res.data.data.map((items: any) => ({
              label: items.displayName,
              value: items.guid,
            }))
          );
          setCountryWithRegion(res.data.data);
        })
        .catch((error) => {
          console.log(error);
        });
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    fetchCountries();
    // eslint-disable-next-line
  }, []);

  const regionHandler = (value: any) => {
    const selectedCountry = countryWithRegion.find(
      (item) => item.guid === value.countryGuid || item.guid === value
    );
    if (selectedCountry) {
      const region = selectedCountry.region;
      setRegion(region);
      updateHttpClientBaseUrl();
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      try {
        // Trim the values before sending them to the server
        const dataToSend = {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          email: values.email.trim(),
          password: values.password,
          companyName: values.companyName.trim(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          countryGuid: values.countryGuid,
        };

        await signup(dataToSend)
          .then((response: any) => {
            if (response.data.status === ERROR_STATUS_KEY) {
              setStatus(response.data.statusDesc);
              setSubmitting(false);
              setLoading(false);
            } else if (response.status === SUCCESS_CODE) {
              navigate("/email-verification", {
                state: { data: { email: dataToSend.email } },
              });
            }
          })
          .catch((error) => {
            formik.setStatus(t("messages:INTERNAL.SERVER.ERROR"));
            setSubmitting(false);
            setLoading(false);
          });
      } catch (error) {
        formik.setStatus(t("messages:INTERNAL.SERVER.ERROR"));
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    PasswordMeterComponent.bootstrap();
  }, []);

  return (
    <div className="w-lg-500px w-sm-500px w-100 mx-auto">
      <form
        className="form  w-100 fv-plugins-bootstrap5 fv-plugins-framework"
        noValidate
        id="n_one_login_signup_form"
        onSubmit={formik.handleSubmit}
      >
        <div className="text-center mb-11">
          <h1 className=" fw-bolder mb-3">Welcome to Bizro</h1>

          <div className="text-gray-500 fw-semibold fs-6">
            {t("messages:AUTH.DESCRIPTION")}
          </div>
        </div>

        {formik.status && (
          <div className="mb-lg-15 alert alert-danger">
            <div className="alert-text text-center font-weight-bold">
              <span dangerouslySetInnerHTML={{ __html: formik.status }} />
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-12 col-sm-6 col-lg-6">
            <InputTextField
              type="text"
              placeholder={t("FIRST.NAME")}
              id="firstName"
              name="firstName"
              label={t("FIRST.NAME")}
              labelClass="required"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={formik.touched.firstName && formik.errors.firstName}
            />
          </div>

          <div className="col-12 col-sm-6 col-lg-6 ">
            <InputTextField
              type="text"
              placeholder={t("LAST.NAME")}
              id="lastName"
              name="lastName"
              label={t("LAST.NAME")}
              labelClass="required"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
            />
          </div>
        </div>

        <InputTextField
          type="text"
          placeholder={t("USER.EMAIL")}
          labelClass="required"
          id="email"
          name="email"
          label={t("USER.EMAIL")}
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
        />
        <InputTextField
          type="text"
          placeholder={t("COMPANY.NAME")}
          id="companyName"
          name="companyName"
          label={t("COMPANY.NAME")}
          labelClass="required"
          value={formik.values.companyName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.companyName && Boolean(formik.errors.companyName)
          }
          helperText={formik.touched.companyName && formik.errors.companyName}
        />

        <SearchableDropdown
          label={t("COUNTRY")}
          labelClass="required"
          placeholder={t("COUNTRY")}
          name="countryGuid"
          value={formik.values.countryGuid ?? ""}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          selectionOptions={country}
          error={
            formik.touched.countryGuid && Boolean(formik.errors.countryGuid)
          }
          helperText={formik.touched.countryGuid && formik.errors.countryGuid}
          getSelectedOption={regionHandler}
        />
        <InputTextField
          type={"password"}
          placeholder={t("PASSWORD")}
          id="password"
          name="password"
          label={t("PASSWORD")}
          labelClass="required"
          passwordVisible={true}
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
        />

        <div className="text-center mt-6">
          <FormButton
            btnLabel={t("SUBMIT")}
            formik={formik}
            isBtnDisabled={formik.isSubmitting || !formik.isValid}
            loading={loading}
            btnColor="dark"
          />

          <div className="text-gray-800 text-center fw-semibold fs-6">
            {t("messages:ALREADY.ACCOUNT")}?{" "}
            <Link to="/login">
              <span className="text-gray-900">{t("LOGIN")}</span>
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
}
